import { useCallback, useEffect, useRef, useState } from 'react';

// Get supported MIME type for recording, preferring WAV for compatibility
const getSupportedMimeType = (): string => {
  const mimeTypes = [
    'audio/wav',
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/ogg',
    'audio/mp4',
  ];

  for (const mimeType of mimeTypes) {
    if (MediaRecorder.isTypeSupported(mimeType)) {
      return mimeType;
    }
  }

  return ''; // Browser will use default
};

// Helper function to detect browser and provide microphone permission instructions
const getMicrophonePermissionInstructions = (): { browser: string; instructions: string } => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes('chrome') || userAgent.includes('chromium')) {
    return {
      browser: 'Chrome',
      instructions:
        'Click the microphone icon in the address bar and select "Allow" for this site. If you don\'t see the icon, click the lock icon or "Not secure" text in the address bar, then change the microphone permission to "Allow".',
    };
  } else if (userAgent.includes('firefox')) {
    return {
      browser: 'Firefox',
      instructions:
        'Click the microphone icon in the address bar and select "Allow" for this site. If you don\'t see the icon, click the shield icon in the address bar, then change the microphone permission to "Allow".',
    };
  } else if (userAgent.includes('safari')) {
    return {
      browser: 'Safari',
      instructions:
        'Go to Safari > Settings > Websites > Microphone, find this site in the list, and change the permission to "Allow". Alternatively, you can click the microphone icon in the address bar if it appears.',
    };
  } else if (userAgent.includes('edge')) {
    return {
      browser: 'Edge',
      instructions:
        'Click the microphone icon in the address bar and select "Allow" for this site. If you don\'t see the icon, click the lock icon in the address bar, then change the microphone permission to "Allow".',
    };
  } else {
    return {
      browser: 'your browser',
      instructions:
        "Please allow microphone access for this site in your browser settings. Look for a microphone icon in the address bar or check your browser's privacy settings.",
    };
  }
};

interface UseAudioRecorderOptions {
  /** Receives the finished blob and the recorder's elapsed seconds once recording stops. */
  onComplete: (blob: Blob, elapsedSeconds: number) => void;
}

/**
 * MediaRecorder lifecycle without any rendering: start/stop, elapsed timer, permission errors.
 * `AudioRecorder` wraps it with the tile/row controls; consumers with their own capture UI
 * (e.g. the job note composer) use the hook directly and render `MicPermissionDialog`.
 */
export const useAudioRecorder = ({ onComplete }: UseAudioRecorderOptions) => {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const elapsedRef = useRef(0);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const [isRecording, setIsRecording] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [permissionError, setPermissionError] = useState<string | null>(null);

  useEffect(() => {
    if (isRecording) {
      recordingTimerRef.current = setInterval(() => {
        setElapsedSeconds((prev) => {
          elapsedRef.current = prev + 1;
          return prev + 1;
        });
      }, 1000);
    } else if (recordingTimerRef.current) {
      clearInterval(recordingTimerRef.current);
      recordingTimerRef.current = null;
    }

    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
    };
  }, [isRecording]);

  const start = useCallback(async () => {
    if (isStarting || isRecording) return; // Prevent double-clicking

    setIsStarting(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });

      const selectedMimeType = getSupportedMimeType();

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: selectedMimeType || undefined,
        audioBitsPerSecond: 128000, // Set a reasonable bitrate
      });

      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = () => {
        try {
          const mimeType = selectedMimeType || 'audio/wav';
          onCompleteRef.current(
            new Blob(chunksRef.current, { type: mimeType }),
            elapsedRef.current,
          );
        } catch (error) {
          console.error('Error processing recorded audio:', error);
          // Create a basic blob as fallback
          onCompleteRef.current(
            new Blob(chunksRef.current, { type: 'audio/wav' }),
            elapsedRef.current,
          );
        } finally {
          stream.getTracks().forEach((track) => track.stop());
        }
      };

      mediaRecorder.start(1000); // Record in 1-second chunks
      elapsedRef.current = 0;
      setElapsedSeconds(0);
      setIsRecording(true);
      setIsStarting(false);
    } catch (error) {
      console.error('Error in startRecording:', error);
      setIsStarting(false);

      // Check if the error is related to microphone permission
      if (error instanceof DOMException) {
        if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
          const { browser, instructions } = getMicrophonePermissionInstructions();
          setPermissionError(
            `Microphone access was denied. To record audio, please allow microphone access in ${browser}. ${instructions}`,
          );
        } else {
          setPermissionError(
            'Unable to access microphone. Please check your microphone settings and try again.',
          );
        }
      } else {
        setPermissionError(
          'Unable to start recording. Please check your microphone settings and try again.',
        );
      }
    }
  }, [isStarting, isRecording]);

  const stop = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
        mediaRecorderRef.current.stream?.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const dismissPermissionError = useCallback(() => setPermissionError(null), []);

  return {
    isRecording,
    isStarting,
    elapsedSeconds,
    permissionError,
    dismissPermissionError,
    start,
    stop,
  };
};
